import type {Painter, PathOp} from '../painter/types'
import type {LvsColorKey} from '../lvs-colors'
import {layoutText, type FontVariant} from './layout'
import {GLYPHS_A} from './glyphs-a'
import {GLYPHS_B} from './glyphs-b'
import {parseGlyphOps} from './glyph-ops'

/**
 * Paint a single glyph at (x, y) in user-space mm at the given scale.
 * The glyph data file stores y-up coordinates (font convention); this
 * function flips y to user-space (y-down) by multiplying y by -scale
 * — the same trick that the SVG <path transform="scale(k -k)"> used,
 * pre-baked into the path ops so any Painter adapter sees y-down mm
 * only. No-op when `d` is empty (unknown glyph or whitespace).
 */
export function paintGlyph(
    p: Painter,
    fill: LvsColorKey,
    d: string,
    x: number,
    y: number,
    scale: number
): void {
    const ops = parseGlyphOps(d)
    if (ops.length === 0) return
    const out: PathOp[] = []
    for (const o of ops) {
        if (o.op === 'm') out.push({op: 'm', x: x + scale * o.x, y: y - scale * o.y})
        else if (o.op === 'l') out.push({op: 'l', x: x + scale * o.x, y: y - scale * o.y})
        else if (o.op === 'c') {
            out.push({
                op: 'c',
                c1x: x + scale * o.c1x,
                c1y: y - scale * o.c1y,
                c2x: x + scale * o.c2x,
                c2y: y - scale * o.c2y,
                x: x + scale * o.x,
                y: y - scale * o.y
            })
        } else {
            out.push({op: 'h'})
        }
    }
    p.fillPathOps(fill, out)
}

/**
 * Paint a full text row at the given baseline in the given fill color.
 */
export function paintTextRow(
    p: Painter,
    fill: LvsColorKey,
    text: string,
    variant: FontVariant,
    x: number,
    baselineY: number,
    h: number
): void {
    const layout = layoutText(text, variant, h)
    const GLYPHS = variant === 'A' ? GLYPHS_A : GLYPHS_B
    for (const placement of layout.glyphs) {
        const d = GLYPHS[placement.char]
        if (!d) continue
        paintGlyph(p, fill, d, x + placement.x, baselineY, placement.scale)
    }
}
